import { Card, Col, Row, Typography } from 'antd';
import { Routes } from 'config/routes';
import {
  DonorStatus,
  UserRole,
  useGetOfficesQuery,
  useGetRegionsQuery,
} from 'gql/types';
import { useCurrentUser } from 'modules/user/hooks/useCurrentUser';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NavigateFunction, useNavigate } from 'react-router-dom';

import { DonorChurnRateCard } from './components/DonorChurnRateCard';
import { DonorRangeCard } from './components/DonorRangeCard';
import { FilterBar } from './components/FilterBar';
import { NewRegistrationsChart } from './components/NewRegistrationsChart';
import { PipelineMetricsStrip } from './components/PipelineMetricsStrip';
import { buildPipelineMetricsCells } from './components/PipelineMetricsStrip/buildPipelineMetricsCells';
import { ShowRatesDblUnitCard } from './components/ShowRatesDblUnitCard';
import { SimulationBanner } from './components/SimulationBanner';
import { ViewOptionsAccordion } from './components/ViewOptionsAccordion';
import { DONOR_LIST_TAB_CONFIGS } from './config/donorListTabConfigs';
import {
  CAL_PERIOD_MAP,
  REGISTRATION_BAR_KEY_MAP,
  REGISTRATION_TABLE_BY_DATE_RANGE,
} from './constants';
import { useMetricTrends } from './hooks/useMetricTrends';
import { useRecruiterFilters } from './hooks/useRecruiterFilters';
import { useRecruiterUserContext } from './hooks/useRecruiterUserContext';
import { useRegionRecruitStats } from './hooks/useRegionRecruitStats';
import { useViewOptions } from './hooks/useViewOptions';
import { RecruiterMetricModals } from './modals/components/modals/RecruiterMetricModals';
import { useRecruiterMetricModals } from './modals/hooks/useRecruiterMetricModals';
import { calcDailyPaceNeeded } from './utils/calcDailyPaceNeeded';
import { calcProgressPercent } from './utils/calcProgressPercent';
import { computePeriodGoalStats } from './utils/computePeriodGoalStats';
import { getRegistrationRanges } from './utils/getRegistrationRanges';
import { recruiterFiltersToUrl } from './utils/recruiterFilters';

import { BRAND_COLORS } from '../../constants/styles';
import {
  getDateRangeBounds,
  getFullPeriodEnd,
} from '../../contexts/PhlebotomistDashboardContext/usePhlebotomistStatsData';
import { useRecruiterDashboard } from '../../contexts/RecruiterDashboardContext';
import { useDonorPipelineStats } from '../../hooks/useDonorPipelineStats';
import { useRecruiterDashboardMetrics } from '../../hooks/useRecruiterDashboardMetrics';
import { useRecruiterDashboardSettings } from '../../hooks/useRecruiterDashboardSettings';
import { useRecruiterProductStats } from '../../hooks/useRecruiterProductStats';
import {
  IRecruiterStatsEntry,
  useRecruiterStats,
} from '../../hooks/useRecruiterStats';
import { DateRangeType } from '../../types/phlebotomist-dashboard';
import { getDateRangeDisplayLabel } from '../../utils/dateRangeLabels';
import {
  IRegionRecruitStats,
  RecruiterStatsCard,
} from '../cards/RecruiterStatsCard';
import UnassignedLocationsModal from '../cards/RecruiterStatsCard/UnassignedLocationsModal';
import { RecruitmentGoalCard } from '../cards/RecruitmentGoalCard';
import { IOfficeOption } from '../filters/OfficeMultiSelect';

const { Title } = Typography;

function navigateToDonorDetails(
  navigate: NavigateFunction,
  params: URLSearchParams
) {
  const qs = params.toString();
  navigate(`${Routes.DashboardsRecruiterDonorDetails}${qs ? `?${qs}` : ''}`);
}

const RecruiterDashboardContent: FC = () => {
  const { t: tGoal } = useTranslation('dashboard.RecruiterDashboard.goalCard');
  const { t: tPipeline } = useTranslation(
    'dashboard.RecruiterDashboard.pipeline'
  );
  const { t: tDonorRange } = useTranslation(
    'dashboard.RecruiterDashboard.donorRange'
  );
  const { contentWidth, setDonorDetailsUrl } = useRecruiterDashboard();
  const { data: currentUserData } = useCurrentUser();
  const navigate = useNavigate();

  const {
    filters,
    setFilters,
    locationIds,
    searchParams,
    handleDateRangeChange,
    handleRegionChange,
    handleOfficeChange,
    handleLocationChange,
  } = useRecruiterFilters();

  const currentUserId = currentUserData?.id || null;

  const applyDefaultDateRange = useCallback((dateRange: DateRangeType) => {
    setFilters((prev) => ({ ...prev, dateRange }));
  }, []);

  const {
    viewOptions,
    defaultDateRange,
    handleViewOptionChange,
    handleDefaultDateRangeChange,
  } = useViewOptions({
    currentUserId,
    searchParams,
    onDefaultDateRangeApply: applyDefaultDateRange,
  });

  // Card layout configuration based on content width
  // >1350: 3 cols (8-8-8) - Working Days | Recruiter Stats | Quarterly
  // 800-1350: 2 rows - Row1: Working Days (12) + Quarterly (12), Row2: Recruiter Stats (24)
  // <800: 3 rows stacked (24 each)
  const cardLayout = useMemo(() => {
    if (contentWidth > 1350) {
      return {
        mode: 'three-col' as const,
        workingDays: { span: 8, order: 1 },
        recruiterStats: { span: 8, order: 2 },
        quarterly: { span: 8, order: 3 },
      };
    } else if (contentWidth > 800) {
      return {
        mode: 'two-row' as const,
        workingDays: { span: 12, order: 1 },
        recruiterStats: { span: 24, order: 3 },
        quarterly: { span: 12, order: 2 },
      };
    } else {
      return {
        mode: 'stacked' as const,
        workingDays: { span: 24, order: 1 },
        recruiterStats: { span: 24, order: 2 },
        quarterly: { span: 24, order: 3 },
      };
    }
  }, [contentWidth]);

  // Fetch recruiter dashboard settings
  const { settings } = useRecruiterDashboardSettings();

  // Keep the Donor Details tab URL in sync with current filters so clicking
  // the tab always loads active-donors with the current filter state.
  useEffect(() => {
    const params = recruiterFiltersToUrl(filters);
    params.set('dataTable', 'active-donors');
    if (settings?.activeDonorStatuses?.length) {
      params.set('activeStatuses', settings.activeDonorStatuses.join(','));
    }
    setDonorDetailsUrl(
      `${Routes.DashboardsRecruiterDonorDetails}?${params.toString()}`
    );
  }, [filters, settings?.activeDonorStatuses, setDonorDetailsUrl]);

  const [registrationPeriodType, setRegistrationPeriodType] = useState<
    'rolling' | 'calendar'
  >('rolling');

  const [isUnassignedModalOpen, setIsUnassignedModalOpen] = useState(false);

  // Fetch donor pipeline stats (active donor count, registrations by period, last week daily)
  const {
    stats: pipelineStats,
    loading: pipelineLoading,
    getRegistrationCount,
    getRegistrationData,
    getYesterdayDonors,
    getTodayDonors,
    getYesterdayLabel,
  } = useDonorPipelineStats({
    skip: !settings,
    regionIds: filters.regionIds,
    officeIds: filters.officeIds,
    locationIds,
    activeDonorStatuses: settings?.activeDonorStatuses,
    pendingDonorStatuses: settings?.pendingDonorStatuses,
    approvedDonorStatuses: settings?.approvedDonorStatuses,
  });

  // Handler to navigate to donor details tab with filters encoded in URL query params.
  // Includes a `dataTable` identifier so the donor-details page can be bookmarked/reloaded.
  const handleDonorListClick = useCallback(
    (configKey: keyof typeof DONOR_LIST_TAB_CONFIGS) => {
      const config = DONOR_LIST_TAB_CONFIGS[configKey];
      if (!config) return;

      // Build query params with dataTable ID + current filters
      const params = recruiterFiltersToUrl(filters);
      params.set('dataTable', config.id);

      const statusesMap: Partial<
        Record<keyof typeof DONOR_LIST_TAB_CONFIGS, DonorStatus[] | undefined>
      > = {
        activeDonors: settings?.activeDonorStatuses,
        singleDonors: settings?.activeDonorStatuses,
        doubleDonors: settings?.activeDonorStatuses,
        pendingDonors: settings?.pendingDonorStatuses,
        approvedDonors: settings?.approvedDonorStatuses,
      };
      const statuses = statusesMap[configKey];
      if (statuses?.length) params.set('activeStatuses', statuses.join(','));

      // Quarterly configs always use quarterCal regardless of current dashboard date range
      if (config.id.endsWith('-quarterly')) {
        params.set('dateRange', 'quarterCal');
      }

      const REGISTRATION_RANGES = getRegistrationRanges();

      const calPeriod = CAL_PERIOD_MAP[config.id];
      const useCalendar = registrationPeriodType === 'calendar' && calPeriod;
      if (useCalendar) {
        const calData = getRegistrationData(calPeriod);
        if (calData?.startDate && calData?.endDate) {
          params.set('registrationStart', calData.startDate);
          params.set('registrationEnd', calData.endDate);
        }
      } else {
        const regRange = REGISTRATION_RANGES[config.id];
        if (regRange) {
          params.set('registrationStart', regRange.start.toISOString());
          params.set('registrationEnd', regRange.end.toISOString());
        }
      }

      const qs = params.toString();
      navigate(`${config.targetRoute}${qs ? `?${qs}` : ''}`);
    },
    [navigate, filters, settings, registrationPeriodType, getRegistrationData]
  );

  // Handler: navigate to recruiter's donor list in Donor Details tab
  const handleRecruiterDonorListClick = useCallback(
    (entry: IRecruiterStatsEntry) => {
      const params = recruiterFiltersToUrl(filters);
      params.set('dataTable', 'recruiter-donors');
      params.set('recruiterId', entry.userId);
      if (entry.regionId) params.set('recruiterRegionId', entry.regionId);
      params.set('recruiterName', `${entry.firstName} ${entry.lastName}`);
      navigateToDonorDetails(navigate, params);
    },
    [navigate, filters]
  );

  // Handler: navigate to recruiter's location list in Donor Details tab
  const handleRecruiterLocationListClick = useCallback(
    (entry: IRecruiterStatsEntry) => {
      const params = recruiterFiltersToUrl(filters);
      params.set('dataTable', 'recruiter-locations');
      params.set('recruiterId', entry.userId);
      if (entry.regionId) params.set('recruiterRegionId', entry.regionId);
      params.set('recruiterName', `${entry.firstName} ${entry.lastName}`);
      if (settings?.activeDonorStatuses?.length) {
        params.set('activeStatuses', settings.activeDonorStatuses.join(','));
      }
      navigateToDonorDetails(navigate, params);
    },
    [navigate, filters, settings?.activeDonorStatuses]
  );

  // Handler: navigate to region-filtered donor list using registration query
  const handleRegionDonorListClick = useCallback(
    (region: IRegionRecruitStats) => {
      const bounds = getDateRangeBounds(filters.dateRange);
      const params = new URLSearchParams();
      params.set(
        'dataTable',
        REGISTRATION_TABLE_BY_DATE_RANGE[filters.dateRange] ??
          'registrations-week'
      );
      params.set('dateRange', filters.dateRange);
      params.set('regionIds', region.regionId);
      params.set('registrationStart', bounds.start.toISOString());
      params.set('registrationEnd', bounds.end.toISOString());
      params.set('recruiterName', region.regionName);
      navigateToDonorDetails(navigate, params);
    },
    [navigate, filters.dateRange]
  );

  const handleRetiredDonorNavigate = useCallback(
    (dataTable: string) => {
      const params = recruiterFiltersToUrl(filters);
      params.set('dataTable', dataTable);
      navigateToDonorDetails(navigate, params);
    },
    [navigate, filters]
  );

  const handleLapsedDonorNavigate = useCallback(
    (dataTable: string) => {
      const params = recruiterFiltersToUrl(filters);
      params.set('dataTable', dataTable);
      if (settings?.activeDonorStatuses?.length) {
        params.set('activeStatuses', settings.activeDonorStatuses.join(','));
      }
      navigateToDonorDetails(navigate, params);
    },
    [navigate, filters, settings?.activeDonorStatuses]
  );

  const {
    userRole,
    roleRestriction,
    isRegionLocked,
    isOfficeLocked,
    isSimulating,
    simulatedUser,
  } = useRecruiterUserContext(setFilters, settings);

  // Fetch product stats with filters
  const { stats: productStats, loading: productLoading } =
    useRecruiterProductStats({
      dateRange: filters.dateRange,
      customDateRange: filters.customDateRange,
      regionIds: filters.regionIds,
      officeIds: filters.officeIds,
      locationIds,
      ffpSglProductIds: settings?.ffpSglProductIds,
      ffpDblProductIds: settings?.ffpDblProductIds,
    });

  // Fetch recruiter dashboard metrics (retention, dropout, churn)
  const { metrics: dashboardMetrics, loading: metricsLoading } =
    useRecruiterDashboardMetrics({
      skip: !settings,
      dateRange: filters.dateRange,
      customDateRange: filters.customDateRange,
      regionIds: filters.regionIds,
      officeIds: filters.officeIds,
      locationIds,
      activeDonorStatuses: settings?.activeDonorStatuses,
      repeatDonationsThreshold: settings?.repeatDonationsThreshold,
      donorDonationFrequency: settings?.donorDonationFrequency,
      donorEligibilityDays: settings?.donorEligibilityDays,
      lapsedDonorBreakpoints: settings?.lapsedDonorBreakpoints,
      retiredDonorBreakpoints: settings?.retiredDonorBreakpoints,
    });

  const dateRangeLabel = getDateRangeDisplayLabel(filters.dateRange);

  // Pin a single "now" for the component's lifetime so the period start
  // (getDateRangeBounds) and period end (getFullPeriodEnd) always resolve the
  // same current day. Without this, two independent new Date() calls can straddle
  // UTC midnight on the 1st of a month and disagree on which month is "last month"
  // (start in April, end in May → an inflated 43-working-day span).
  const now = useMemo(() => new Date(), []);

  const dateBounds = useMemo(
    () => getDateRangeBounds(filters.dateRange, filters.customDateRange, now),
    [filters.dateRange, filters.customDateRange, now]
  );

  const quarterlyDateBounds = useMemo(
    () => getDateRangeBounds('quarterCal', undefined, now),
    [now]
  );

  const modals = useRecruiterMetricModals({
    dashboardMetrics,
    settings,
    dateBounds,
    quarterlyDateBounds,
    filters,
    locationIds,
    dateRangeLabel,
  });

  const regionsQuery = useGetRegionsQuery();
  const officesQuery = useGetOfficesQuery();

  const {
    ratioTrend,
    ratioTrendColor,
    churnTrend,
    churnTrendColor,
    churnQuarterlyTrend,
    churnQuarterlyTrendColor,
    showRateTrend,
    showRateTrendColor,
    retentionTrend,
    retentionTrendColor,
    dropoutTrend,
    dropoutTrendColor,
  } = useMetricTrends(filters, dateBounds.start, dateBounds.end, settings);

  const fullPeriodEnd = useMemo(
    () => getFullPeriodEnd(filters.dateRange, filters.customDateRange, now),
    [filters.dateRange, filters.customDateRange, now]
  );

  const regions = useMemo(
    () =>
      regionsQuery.data?.getRegions.map((r) => ({
        id: r.id,
        name: r.name,
      })) || [],
    [regionsQuery.data]
  );

  const offices: IOfficeOption[] = useMemo(
    () =>
      officesQuery.data?.getOffices.map((o) => ({
        id: o.id,
        name: o.name,
        regionId: o.region?.id || '',
      })) || [],
    [officesQuery.data]
  );

  // Calculate the number of offices for goal calculation
  // Daily pRBC Goal is per-office, so we multiply by the number of offices included
  const officeCount = useMemo(() => {
    // If specific offices are selected, use that count
    if (filters.officeIds.length > 0) {
      return filters.officeIds.length;
    }
    // If regions are selected but no specific offices, count offices in those regions
    if (filters.regionIds.length > 0) {
      return offices.filter((o) => filters.regionIds.includes(o.regionId))
        .length;
    }
    // If nothing selected (all regions/offices), use total office count
    return offices.length || 1; // Default to 1 to avoid 0 goal when data hasn't loaded
  }, [filters.officeIds, filters.regionIds, offices]);

  const dailyGoal = (settings?.dayPrbcGoal ?? 0) * officeCount;

  const {
    workingDaysCount,
    workingDaysLeftCount,
    workingDaysElapsed: monthlyWorkingDaysElapsed,
    goal: prbcGoal,
    pacePercent: monthlyPacePercent,
    status: monthlyStatus,
    isPeriodClosed,
  } = useMemo(
    () =>
      computePeriodGoalStats(
        dateBounds.start,
        fullPeriodEnd,
        settings,
        productStats.totalPRBC,
        dailyGoal
      ),
    [
      dateBounds.start,
      fullPeriodEnd,
      settings,
      productStats.totalPRBC,
      dailyGoal,
    ]
  );

  // Recruiter Stats (replaces leaderboard on Dashboard tab)
  const {
    entries: recruiterStatsEntries,
    unassignedLocationCount,
    unassignedLocations,
    loading: recruiterStatsLoading,
  } = useRecruiterStats({
    dateRange: filters.dateRange,
    regionIds: filters.regionIds,
    activeLocationThresholdDays: settings?.activeLocationThresholdDays ?? 90,
  });

  const regionRecruitStats = useRegionRecruitStats({
    dateRange: filters.dateRange,
    regionIds: filters.regionIds,
    activeLocationThresholdDays: settings?.activeLocationThresholdDays ?? 90,
    skip: !roleRestriction.showLocationRecruitStats,
  });

  // Fetch quarterly product stats (always uses 'quarterCal' regardless of filter)
  const { stats: quarterlyProductStats, loading: quarterlyProductLoading } =
    useRecruiterProductStats({
      dateRange: 'quarterCal',
      regionIds: filters.regionIds,
      officeIds: filters.officeIds,
      locationIds,
    });

  const quarterlyFullPeriodEnd = useMemo(
    () => getFullPeriodEnd('quarterCal', undefined, now),
    [now]
  );
  const quarterlyDateRangeLabel = getDateRangeDisplayLabel('quarterCal');

  const {
    workingDaysCount: quarterlyWorkingDaysCount,
    workingDaysLeftCount: quarterlyWorkingDaysLeftCount,
    workingDaysElapsed: quarterlyWorkingDaysElapsed,
    goal: quarterlyPrbcGoal,
    pacePercent: quarterlyPacePercent,
    status: quarterlyStatus,
    isPeriodClosed: isQuarterlyPeriodClosed,
  } = useMemo(
    () =>
      computePeriodGoalStats(
        quarterlyDateBounds.start,
        quarterlyFullPeriodEnd,
        settings,
        quarterlyProductStats.totalPRBC,
        dailyGoal
      ),
    [
      quarterlyDateBounds.start,
      quarterlyFullPeriodEnd,
      settings,
      quarterlyProductStats.totalPRBC,
      dailyGoal,
    ]
  );

  // Dynamic footer text for goal cards based on filter selection
  const goalCardFooterText = useMemo(() => {
    if (filters.officeIds.length > 0) {
      const names = offices
        .filter((o) => filters.officeIds.includes(o.id))
        .map((o) => o.name);
      return tGoal('footerOffice', {
        count: names.length,
        names: names.join(', '),
      });
    }
    if (filters.regionIds.length > 0) {
      const names = regions
        .filter((r) => filters.regionIds.includes(r.id))
        .map((r) => r.name);
      return tGoal('footerRegion', {
        count: names.length,
        names: names.join(', '),
      });
    }
    return tGoal('footerAllOffices');
  }, [tGoal, filters.officeIds, filters.regionIds, offices, regions]);

  const pipelineCells = useMemo(
    () =>
      buildPipelineMetricsCells({
        pipelineStats,
        pipelineLoading,
        dashboardMetrics,
        metricsLoading,
        contentWidth,
        t: tPipeline,
        trends: {
          retentionTrend,
          retentionTrendColor,
          dropoutTrend,
          dropoutTrendColor,
          ratioTrend,
          ratioTrendColor,
        },
        handlers: {
          onActiveDonorsClick: () => handleDonorListClick('activeDonors'),
          onPendingDonorsClick: () => handleDonorListClick('pendingDonors'),
          onApprovedDonorsClick: () => handleDonorListClick('approvedDonors'),
          onRetentionDetailsClick: () => modals.open('retention'),
          onDropoutDetailsClick: () => modals.open('dropout'),
          onRatioDetailsClick: () => modals.open('ratio'),
        },
      }),
    [
      pipelineStats,
      pipelineLoading,
      dashboardMetrics,
      metricsLoading,
      retentionTrend,
      retentionTrendColor,
      dropoutTrend,
      dropoutTrendColor,
      ratioTrend,
      ratioTrendColor,
      contentWidth,
      handleDonorListClick,
      tPipeline,
      modals.open,
    ]
  );

  return (
    <>
      {isSimulating && settings?.simulateRole && (
        <SimulationBanner
          simulateRole={settings.simulateRole}
          simulatedUser={simulatedUser}
        />
      )}
      <Row gutter={[16, 16]}>
        {/* Filter Bar */}
        <Col span={24}>
          <FilterBar
            contentWidth={contentWidth}
            filters={filters}
            regions={regions}
            offices={offices}
            regionsLoading={regionsQuery.loading}
            officesLoading={officesQuery.loading}
            isRegionLocked={isRegionLocked}
            isOfficeLocked={isOfficeLocked}
            roleRestriction={roleRestriction}
            onDateRangeChange={handleDateRangeChange}
            onRegionChange={handleRegionChange}
            onOfficeChange={handleOfficeChange}
            onLocationChange={handleLocationChange}
          />
        </Col>

        {/* Visibility / Default Options Accordion */}
        <Col span={24}>
          <ViewOptionsAccordion
            viewOptions={viewOptions}
            defaultDateRange={defaultDateRange}
            onViewOptionChange={handleViewOptionChange}
            onDefaultDateRangeChange={handleDefaultDateRangeChange}
          />
        </Col>

        {/* Monthly/Period Recruitment Goal */}
        {viewOptions.recruitmentGoals && (
          <Col
            span={cardLayout.workingDays.span}
            style={{ order: cardLayout.workingDays.order, display: 'flex' }}
          >
            <Card style={{ flex: 1 }}>
              <RecruitmentGoalCard
                title={tGoal('periodTitle', { dateRangeLabel })}
                unitsCollected={productStats.totalPRBC}
                unitsLabel={tGoal('prbcCollected')}
                secondaryValue={productStats.totalFFP}
                secondaryLabel={tGoal('ffpCollected')}
                onSecondaryClick={() => handleDonorListClick('ffpDonations')}
                secondaryClickTooltip={tGoal('ffpUnitsTooltip')}
                goal={prbcGoal}
                goalLabel={tGoal('prbcGoal')}
                goalTooltip={tGoal('goalTooltipPeriod', {
                  dailyTarget: settings?.dayPrbcGoal ?? 0,
                  workingDays: workingDaysCount,
                  officeCount,
                })}
                progressPercent={calcProgressPercent(
                  productStats.totalPRBC,
                  prbcGoal
                )}
                statusInfo={monthlyStatus}
                statusTooltip={
                  monthlyStatus
                    ? tGoal('statusTooltipPeriod', {
                        statusLabel: monthlyStatus.label,
                        pacePercent: monthlyPacePercent,
                      })
                    : undefined
                }
                dailyPaceNeeded={calcDailyPaceNeeded(
                  productStats.totalPRBC,
                  prbcGoal,
                  workingDaysLeftCount,
                  isPeriodClosed
                )}
                dailyPaceLabel={tGoal('dailyPaceLabel', { dateRangeLabel })}
                dailyPaceFontSize={contentWidth < 400 ? 12 : 15}
                footerText={goalCardFooterText}
                showDecorations={true}
                onUnitsClick={() => handleDonorListClick('prbcDonations')}
                unitsClickTooltip={tGoal('prbcUnitsTooltip')}
                accentColor={BRAND_COLORS.secondary.steelBlue}
                loading={productLoading}
                daysElapsed={monthlyWorkingDaysElapsed}
                totalDays={workingDaysCount}
                windowWidth={contentWidth}
              />
            </Card>
          </Col>
        )}

        {/* Recruiter Stats */}
        {viewOptions.recruiterStats && (
          <Col
            span={cardLayout.recruiterStats.span}
            style={{ order: cardLayout.recruiterStats.order, display: 'flex' }}
          >
            <Card
              style={{
                flex: 1,
                padding: 0,
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
              }}
              bodyStyle={{
                padding: 0,
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
              }}
              bordered={false}
            >
              <RecruiterStatsCard
                entries={recruiterStatsEntries}
                loading={recruiterStatsLoading}
                isAdmin={userRole === UserRole.Admin}
                unassignedLocationCount={unassignedLocationCount}
                unassignedLocations={unassignedLocations}
                settings={settings}
                dateRangeLabel={getDateRangeDisplayLabel(filters.dateRange)}
                dateRangeStart={dateBounds.start}
                dateRangeEnd={dateBounds.end}
                onUnassignedClick={() => setIsUnassignedModalOpen(true)}
                onDonorListClick={handleRecruiterDonorListClick}
                onLocationListClick={handleRecruiterLocationListClick}
                showLocationRecruitStats={
                  roleRestriction.showLocationRecruitStats
                }
                regionRecruitStats={regionRecruitStats}
                onRegionDonorListClick={handleRegionDonorListClick}
              />
            </Card>
          </Col>
        )}
        {/* Quarterly Recruitment Goal */}
        {viewOptions.quarterlyGoal && (
          <Col
            span={cardLayout.quarterly.span}
            style={{ order: cardLayout.quarterly.order, display: 'flex' }}
          >
            <Card style={{ flex: 1 }}>
              <RecruitmentGoalCard
                title={tGoal('quarterlyTitle')}
                unitsCollected={quarterlyProductStats.totalPRBC}
                goal={quarterlyPrbcGoal}
                goalLabel={tGoal('quarterlyGoal')}
                goalTooltip={tGoal('goalTooltipQuarterly', {
                  dailyTarget: settings?.dayPrbcGoal ?? 0,
                  workingDays: quarterlyWorkingDaysCount,
                  officeCount,
                })}
                progressPercent={calcProgressPercent(
                  quarterlyProductStats.totalPRBC,
                  quarterlyPrbcGoal
                )}
                statusInfo={quarterlyStatus}
                statusTooltip={
                  quarterlyStatus
                    ? tGoal('statusTooltipQuarterly', {
                        statusLabel: quarterlyStatus.label,
                        pacePercent: quarterlyPacePercent,
                      })
                    : undefined
                }
                dailyPaceNeeded={calcDailyPaceNeeded(
                  quarterlyProductStats.totalPRBC,
                  quarterlyPrbcGoal,
                  quarterlyWorkingDaysLeftCount,
                  isQuarterlyPeriodClosed
                )}
                dailyPaceLabel={tGoal('dailyPaceLabel', {
                  dateRangeLabel: quarterlyDateRangeLabel,
                })}
                dailyPaceFontSize={contentWidth < 400 ? 12 : 15}
                footerText={goalCardFooterText}
                showDecorations={true}
                onUnitsClick={() =>
                  handleDonorListClick('prbcDonationsQuarterly')
                }
                unitsClickTooltip={tGoal('prbcUnitsQuarterlyTooltip')}
                accentColor="#4f6d7a"
                loading={quarterlyProductLoading}
                daysElapsed={quarterlyWorkingDaysElapsed}
                totalDays={quarterlyWorkingDaysCount}
                windowWidth={contentWidth}
              />
            </Card>
          </Col>
        )}

        {/* Donor Pipeline Health Summary Section */}
        <Col span={24} style={{ order: 10 }}>
          <Card bordered={false}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 16,
                marginBottom: 16,
              }}
            >
              <div style={{ flex: 1, height: 1, background: '#d9d9d9' }} />
              <Title level={4} style={{ margin: 0 }}>
                {tPipeline('sectionTitle')}
              </Title>
              <div style={{ flex: 1, height: 1, background: '#d9d9d9' }} />
            </div>

            {/* Donor Pipeline Metrics Strip */}
            {viewOptions.donorPipelineStrip && (
              <PipelineMetricsStrip
                contentWidth={contentWidth}
                cells={pipelineCells}
              />
            )}

            <Row gutter={[16, 16]}>
              {viewOptions.newRegistrationsChart && (
                <Col span={contentWidth < 900 ? 24 : 12}>
                  <NewRegistrationsChart
                    periodType={registrationPeriodType}
                    onPeriodTypeChange={setRegistrationPeriodType}
                    pipelineStats={pipelineStats}
                    pipelineLoading={pipelineLoading}
                    getRegistrationCount={getRegistrationCount}
                    getRegistrationData={getRegistrationData}
                    getYesterdayDonors={getYesterdayDonors}
                    getTodayDonors={getTodayDonors}
                    getYesterdayLabel={getYesterdayLabel}
                    onBarClick={(name) => {
                      handleDonorListClick(REGISTRATION_BAR_KEY_MAP[name]);
                    }}
                    onNewDonorsClick={() =>
                      handleDonorListClick('newDonorsYesterday')
                    }
                  />
                </Col>
              )}

              {viewOptions.donorChurnRate && (
                <Col span={contentWidth < 900 ? 24 : 12}>
                  <DonorChurnRateCard
                    metricsLoading={metricsLoading}
                    periodChurnRate={dashboardMetrics?.periodChurnRate ?? 0}
                    quarterlyChurnRate={
                      dashboardMetrics?.quarterlyChurnRate ?? 0
                    }
                    periodChurnStatus={modals.periodChurnStatus}
                    quarterlyChurnStatus={modals.quarterlyChurnStatus}
                    dateRangeLabel={dateRangeLabel}
                    churnTrend={churnTrend}
                    churnTrendColor={churnTrendColor}
                    churnQuarterlyTrend={churnQuarterlyTrend}
                    churnQuarterlyTrendColor={churnQuarterlyTrendColor}
                    contentWidth={contentWidth}
                    onPeriodDetailsClick={() => modals.open('churn-period')}
                    onQuarterlyDetailsClick={() =>
                      modals.open('churn-quarterly')
                    }
                  />
                </Col>
              )}
            </Row>

            {/* Second Row: Show-rates & Lapsed Donors */}
            <Row gutter={[16, 16]} style={{ marginTop: 16 }} align="stretch">
              {/* Donor Show-rates / Dbl Unit Donors - 33% width (stacked) */}
              {viewOptions.showRatesDblUnit && (
                <Col xs={24} lg={8} xl={8} style={{ display: 'flex' }}>
                  <ShowRatesDblUnitCard
                    metricsLoading={metricsLoading}
                    showRate={dashboardMetrics?.showRate ?? 0}
                    showRateStatus={modals.showRateStatus}
                    showRateTrend={showRateTrend}
                    showRateTrendColor={showRateTrendColor}
                    singleDonorCount={dashboardMetrics?.singleDonorCount ?? 0}
                    doubleDonorCount={dashboardMetrics?.doubleDonorCount ?? 0}
                    contentWidth={contentWidth}
                    onShowRateDetailsClick={() => modals.open('showRate')}
                    onSingleDonorsClick={() =>
                      handleDonorListClick('singleDonors')
                    }
                    onDoubleDonorsClick={() =>
                      handleDonorListClick('doubleDonors')
                    }
                  />
                </Col>
              )}

              {/* Retired Donors (Unresponsive) - 33% width */}
              {viewOptions.retiredDonors && (
                <Col xs={24} lg={8} xl={8} style={{ display: 'flex' }}>
                  <DonorRangeCard
                    title={tDonorRange('retired')}
                    variant="retired"
                    breakpoints={
                      settings?.retiredDonorBreakpoints ?? [30, 60, 90]
                    }
                    labels={
                      settings?.retiredDonorLabels ?? [
                        tDonorRange('recent'),
                        tDonorRange('moderate'),
                        tDonorRange('extended'),
                        tDonorRange('critical'),
                      ]
                    }
                    counts={dashboardMetrics?.retiredDonorCounts}
                    loading={metricsLoading}
                    contentWidth={contentWidth}
                    onNavigate={handleRetiredDonorNavigate}
                  />
                </Col>
              )}

              {/* Lapsed Donors Needing Outreach - 33% width */}
              {viewOptions.lapsedDonors && (
                <Col xs={24} lg={8} xl={8} style={{ display: 'flex' }}>
                  <DonorRangeCard
                    title={tDonorRange('lapsed')}
                    variant="lapsed"
                    breakpoints={
                      settings?.lapsedDonorBreakpoints ?? [30, 60, 90]
                    }
                    labels={
                      settings?.lapsedDonorLabels ?? [
                        tDonorRange('recent'),
                        tDonorRange('moderate'),
                        tDonorRange('extended'),
                        tDonorRange('critical'),
                      ]
                    }
                    counts={dashboardMetrics?.lapsedDonorCounts}
                    loading={metricsLoading}
                    contentWidth={contentWidth}
                    onNavigate={handleLapsedDonorNavigate}
                  />
                </Col>
              )}
            </Row>
          </Card>
        </Col>
      </Row>

      <RecruiterMetricModals modals={modals} settings={settings} />

      <UnassignedLocationsModal
        visible={isUnassignedModalOpen}
        onClose={() => setIsUnassignedModalOpen(false)}
        locations={unassignedLocations}
      />
    </>
  );
};

export default RecruiterDashboardContent;
